import {
  useHPTARGeneralMessage,
  useHPTARInvalids,
  useHPTARs,
  useInfiniteKmzInvalids,
} from "@/api/hptar";
import { FormContainer } from "@/components/form/form-container";
import { FormFooter } from "@/components/form/form-footer";
import { DefaultButton, SubmitButton } from "@/components/ui/button";
import { H4 } from "@/components/ui/typography";
import { MISSING_ID_IN_ROUTE, ROLE } from "@/utils/constant";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { useEffect, useId } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { DocumentUploadSection } from "./document-upload-section/DocumentUploadSection";
import { DataCreationSection } from "./data-creation-section/DataCreationSection";
import {
  SubmitSupportingDocumentsFormSchema,
  submitSupportingDocumentsFormSchema,
  useDocumentPrepare,
} from "./utils/useDocumentPrepare";
import { StatusFeeder } from "./hptar-section/StatusFeeder";
import {
  isHPTARValidated,
  isGPONValidatedByRFS,
  isNNEvidenceValidatedByRFS,
  isOPMValidatedByRFS,
} from "./utils/validation";
import { useDocumentCancel } from "./utils/useDocumentCancel";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { useRFS } from "@/api/rfs";
import useCheckingProfileValidations from "./utils/useCheckingProfileValidations";
import useDefaultSupportingDocuments from "./utils/useDefaultSupportingDocuments";

export function DocumentPreparationForm() {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { roles } = useCurrentUser();
  const defaultSupportingDocuments = useDefaultSupportingDocuments({
    rfsId: id,
  });

  const formId = useId();
  const form = useForm<SubmitSupportingDocumentsFormSchema>({
    resolver: zodResolver(submitSupportingDocumentsFormSchema),
    defaultValues: defaultSupportingDocuments,
  });

  const { data: rfs } = useRFS({ id });

  const { data: hptars } = useHPTARs({ rfsId: id });
  const { data: hptarInvalids } = useHPTARInvalids({ rfsId: id });
  const { data: generalMessages } = useHPTARGeneralMessage({
    rfsId: id,
  });
  const { data: kmzInvalids } = useInfiniteKmzInvalids({ rfsId: id });

  const { fields } = useCheckingProfileValidations({
    rfs,
  });

  const { handlePrepare } = useDocumentPrepare({ id, fields, form });
  const { handleCancel } = useDocumentCancel({ id });

  const isDocumentUploadValidated = fields
    ? fields.every(
        (field) => field.status === "Validated" || field.status === "Optional",
      )
    : false;

  const isViewOnly = hasCommonElement(roles, [ROLE.VENDOR_VIEW]);
  const isSubmitDisabled =
    !isOPMValidatedByRFS(rfs) ||
    !isNNEvidenceValidatedByRFS(rfs) ||
    !isGPONValidatedByRFS(rfs) ||
    !isHPTARValidated({
      hptars,
      hptarInvalids,
      hptarGeneralMessages: generalMessages,
      kmzInvalids: kmzInvalids?.pages?.[0]?.content,
    }) ||
    !isDocumentUploadValidated ||
    isViewOnly;

  const isCancelable = hasCommonElement(roles, [ROLE.ADMINISTRATOR]);

  useEffect(() => {
    if (defaultSupportingDocuments) {
      form.reset(defaultSupportingDocuments);
    }
  }, [defaultSupportingDocuments, form]);

  return (
    <>
      <FormContainer id={formId} form={form} onSubmit={handlePrepare}>
        <H4>Step 1 - Preparation & Validate HPTAR</H4>

        <DataCreationSection />

        <hr />

        <H4>Step 2 - Upload & Validate Supporting Documents</H4>

        {fields && <DocumentUploadSection fields={fields} />}

        <StatusFeeder />
      </FormContainer>

      <FormFooter>
        {isCancelable && (
          <DefaultButton
            mood="axiata-reddish-outline"
            type="button"
            onClick={handleCancel}
          >
            Cancel
          </DefaultButton>
        )}
        <SubmitButton form={formId} disabled={isSubmitDisabled} />
      </FormFooter>
    </>
  );
}
